import { InputAdornment } from '@mui/material';
import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';

import { useAppDispatch } from 'store/store';
import {
	getUserDocuments,
	uploadDocumentUrl,
	verifyDocumentUpload,
} from 'store/user-service/asyncActions';

import { NamespacesList } from 'core/enums/common';
import { errorNotify } from 'core/helpers/toasts';
import { UserFile } from 'core/types/base';

import { ReactComponent as DocumentUpload } from 'assets/icons/document-upload.svg';
import { ReactComponent as Search } from 'assets/icons/search.svg';

import { readFileAsArrayBuffer, uploadDocumentErrors, uploadFileToCloud } from './helpers';
import * as Styled from './styled';

interface DocumentHeaderProps {
	handleActionFileProcessingErrorModal: () => void;
	handleEmbedDocument: (selectedFile: UserFile) => Promise<void>;
}

const maxSizeMB = 5;

export const DocumentHeader: FC<DocumentHeaderProps> = ({
	handleActionFileProcessingErrorModal,
	handleEmbedDocument,
}) => {
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);
	const { t: profileTranslate } = useTranslation(NamespacesList['profilePage']);

	const dispatch = useAppDispatch();
	const inputRef = useRef<null | HTMLInputElement>(null);

	const [uploadLoading, setUploadLoading] = useState(false);

	const downloadFile = () => {
		if (inputRef && inputRef.current) {
			inputRef.current.click();
		}
	};

	const handlePdfUpload = async (e: any) => {
		setUploadLoading(true);
		const selectedFile = e.target.files[0];
		if (selectedFile) {
			const fileSizeMB = selectedFile.size / (1024 * 1024);
			if (fileSizeMB > maxSizeMB) {
				errorNotify(
					commonTranslate('errors.uploadedFileTooLargeTitle'),
					commonTranslate('errors.uploadedFileTooLargeDescription')
				);
				setUploadLoading(false);
				return; // Exit the function if file size exceeds the limit
			}

			const documentName = selectedFile.name.replace('.pdf', '');
			try {
				const cloudURL = await dispatch(
					uploadDocumentUrl({
						fileName: documentName,
					})
				).unwrap();

				if (cloudURL) {
					try {
						const arrayBuffer = await readFileAsArrayBuffer(selectedFile);
						const binaryArray = new Uint8Array(arrayBuffer);

						await uploadFileToCloud(cloudURL, binaryArray);

						await dispatch(verifyDocumentUpload({ fileName: documentName })).unwrap();
						setUploadLoading(false);
						await dispatch(getUserDocuments({ currentPageNumber: 1, recordsPerPage: 5 })).unwrap();
						await handleEmbedDocument(selectedFile);
					} catch (error) {
						handleActionFileProcessingErrorModal();
						setUploadLoading(false);
					}
				}
			} catch (error) {
				if (error === 'File already exist') {
					const { title, description } = uploadDocumentErrors(commonTranslate).sameFile;
					setUploadLoading(false);
					errorNotify(title, description);
				}
			}
		}
	};

	return (
		<Styled.DocumentHeader>
			<Styled.SearchField
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<Search />
						</InputAdornment>
					),
					style: {
						paddingLeft: '12px',
						height: '44px',
						borderRadius: '8px',
						background: '#F3F4F5',
					},
				}}
				placeholder={commonTranslate('inputs.searchDocumentInputPlaceholder') as string}
				variant="outlined"
				color="primary"
			/>
			<Styled.AddDocumentButton variant="outlined" onClick={downloadFile} disabled={uploadLoading}>
				{!uploadLoading ? (
					<DocumentUpload />
				) : (
					<Oval
						height={20}
						width={20}
						color="rgba(0, 0, 0, 0.26)"
						wrapperStyle={{}}
						wrapperClass=""
						visible={true}
						ariaLabel="oval-loading"
						secondaryColor="rgba(0, 0, 0, 0.26)"
						strokeWidth={5}
						strokeWidthSecondary={5}
					/>
				)}{' '}
				{profileTranslate('text.loadDocument')}{' '}
				<Styled.VisuallyHiddenInput
					type="file"
					ref={inputRef}
					onChange={handlePdfUpload}
					accept="application/pdf"
				/>
			</Styled.AddDocumentButton>
		</Styled.DocumentHeader>
	);
};
